import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext(null);

// Función para aplicar colores de tema a CSS variables
const applyThemeColors = (colores) => {
  if (!colores) return;
  
  const root = document.documentElement;
  
  // Convertir hex a HSL para las variables de Tailwind/Shadcn
  const hexToHSL = (hex) => {
    // Validar que sea un hex válido
    if (!hex || typeof hex !== 'string' || !hex.startsWith('#')) {
      return '0 0% 50%';
    }
    
    const r = parseInt(hex.slice(1, 3), 16) / 255;
    const g = parseInt(hex.slice(3, 5), 16) / 255;
    const b = parseInt(hex.slice(5, 7), 16) / 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h, s, l = (max + min) / 2;

    if (max === min) {
      h = s = 0;
    } else {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
        case g: h = ((b - r) / d + 2) / 6; break;
        case b: h = ((r - g) / d + 4) / 6; break;
        default: h = 0;
      }
    }

    return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
  };

  // Aplicar colores principales
  if (colores.primario) {
    root.style.setProperty('--theme-primary', colores.primario);
    root.style.setProperty('--theme-primary-hsl', hexToHSL(colores.primario));
  }
  if (colores.secundario) {
    root.style.setProperty('--theme-secondary', colores.secundario);
    root.style.setProperty('--theme-secondary-hsl', hexToHSL(colores.secundario));
  }
  if (colores.terciario) {
    root.style.setProperty('--theme-tertiary', colores.terciario);
    root.style.setProperty('--theme-tertiary-hsl', hexToHSL(colores.terciario));
  }
  if (colores.cuaternario) {
    root.style.setProperty('--theme-quaternary', colores.cuaternario);
    root.style.setProperty('--theme-quaternary-hsl', hexToHSL(colores.cuaternario));
  }
  
  // Aplicar colores de fondo
  if (colores.fondo) {
    root.style.setProperty('--theme-background', colores.fondo);
  }
  if (colores.superficie) {
    root.style.setProperty('--theme-surface', colores.superficie);
  }
  if (colores.fondo_cabecera) {
    root.style.setProperty('--theme-header-public', colores.fondo_cabecera);
  }
  
  // Aplicar colores de texto
  if (colores.texto) {
    root.style.setProperty('--theme-text', colores.texto);
  }
  if (colores.texto_secundario) {
    root.style.setProperty('--theme-text-secondary', colores.texto_secundario);
  }
  
  // Aplicar color de error
  if (colores.error) {
    root.style.setProperty('--theme-error', colores.error);
  }
  
  // Compatibilidad con formato antiguo (3 colores)
  if (colores.acento && !colores.terciario) {
    root.style.setProperty('--theme-tertiary', colores.acento);
    root.style.setProperty('--theme-tertiary-hsl', hexToHSL(colores.acento));
  }
};

// Función para resetear colores por defecto (Imagina)
const resetThemeColors = () => {
  const root = document.documentElement;
  // Colores principales
  root.style.setProperty('--theme-primary', '#C9A85C');
  root.style.setProperty('--theme-secondary', '#C5B8A2');
  root.style.setProperty('--theme-tertiary', '#5A7A7A');
  root.style.setProperty('--theme-quaternary', '#5A7A7A');
  // Fondos
  root.style.setProperty('--theme-background', '#FFFFFF');
  root.style.setProperty('--theme-surface', '#F5F5F5');
  root.style.setProperty('--theme-header-public', '#333333');
  // Textos
  root.style.setProperty('--theme-text', '#333333');
  root.style.setProperty('--theme-text-secondary', '#666666');
  // Estados
  root.style.setProperty('--theme-error', '#DC3545');
  // HSL versions
  root.style.setProperty('--theme-primary-hsl', '43 50% 57%');
  root.style.setProperty('--theme-secondary-hsl', '36 24% 70%');
  root.style.setProperty('--theme-tertiary-hsl', '180 15% 42%');
  root.style.setProperty('--theme-quaternary-hsl', '180 15% 42%');
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [empresa, setEmpresa] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('ldrv_token'));
  const [loading, setLoading] = useState(true);
  const [requiereCambioPassword, setRequiereCambioPassword] = useState(false);

  const api = axios.create({
    baseURL: `${API_URL}/api`,
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });

  // Interceptor para agregar token
  api.interceptors.request.use((config) => {
    const currentToken = localStorage.getItem('ldrv_token');
    if (currentToken) {
      config.headers.Authorization = `Bearer ${currentToken}`;
    }
    return config;
  });

  // Interceptor para manejar errores 401
  api.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.status === 401) {
        logout();
      }
      return Promise.reject(error);
    }
  );

  const fetchUser = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await api.get('/auth/me');
      setUser(response.data.user);
      setEmpresa(response.data.empresa);
      setRequiereCambioPassword(response.data.cambio_password_requerido || false);
      
      // Aplicar colores de la empresa
      if (response.data.empresa?.colores_tema) {
        applyThemeColors(response.data.empresa.colores_tema);
      }
    } catch (error) {
      console.error('Error fetching user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  // Aplicar colores cuando cambia la empresa
  useEffect(() => {
    if (empresa?.colores_tema) {
      applyThemeColors(empresa.colores_tema);
    } else {
      resetThemeColors();
    }
  }, [empresa]);

  const login = async (empresaCodigo, correo, password) => {
    const response = await api.post('/auth/login', {
      empresa_codigo: empresaCodigo || null,
      correo,
      password,
    });

    const { token: newToken, user: userData, empresa: empresaData, cambio_password_requerido } = response.data;

    localStorage.setItem('ldrv_token', newToken);
    // Guardar código de empresa para redirección al logout
    if (empresaCodigo && empresaCodigo !== '__superadmin__') {
      localStorage.setItem('ldrv_empresa_codigo', empresaCodigo);
    } else {
      localStorage.removeItem('ldrv_empresa_codigo');
    }
    setToken(newToken);
    setUser(userData);
    setEmpresa(empresaData);
    setRequiereCambioPassword(cambio_password_requerido || false);
    
    // Aplicar colores de la empresa
    if (empresaData?.colores_tema) {
      applyThemeColors(empresaData.colores_tema);
    }

    return response.data;
  };

  const logout = () => {
    localStorage.removeItem('ldrv_token');
    setToken(null);
    setUser(null);
    setEmpresa(null);
    setRequiereCambioPassword(false);
    resetThemeColors();
  };

  const updateEmpresa = (newEmpresa) => {
    setEmpresa(newEmpresa);
    if (newEmpresa?.colores_tema) {
      applyThemeColors(newEmpresa.colores_tema);
    }
  };

  const hasRole = (...roles) => {
    return user && roles.includes(user.rol);
  };

  const isSuperAdmin = () => user?.rol === 'SUPERADMIN';
  const isAdmin = () => hasRole('ADMINISTRADOR', 'SUPERADMIN');
  const isSupervisor = () => hasRole('SUPERVISOR', 'ADMINISTRADOR', 'SUPERADMIN');

  const value = {
    user,
    empresa,
    token,
    loading,
    login,
    logout,
    updateEmpresa,
    hasRole,
    isSuperAdmin,
    isAdmin,
    isSupervisor,
    requiereCambioPassword,
    setRequiereCambioPassword,
    api,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
